import { Box, BoxProps } from '@mui/material';

/**
 * Render a country's flag as a Unicode regional-indicator emoji
 * (e.g. 'DE' → 🇩🇪).  Works without any external dependency on every
 * modern browser; falls back to the plain alpha-2 text if the host
 * font lacks emoji glyphs.
 */
type Props = Omit<BoxProps, 'children'> & {
  alpha2: string;
  /** Visual size in rem.  Default keeps the flag in line with body text. */
  size?: number | string;
};

export function Flag({ alpha2, size = '1.5rem', sx, ...rest }: Props) {
  return (
    <Box
      component="span"
      role="img"
      aria-label={alpha2}
      sx={{
        fontSize: size,
        // Force the emoji-flags font on Windows where Segoe UI Emoji has
        // them but the default Segoe UI doesn't.
        fontFamily:
          '"Twemoji Mozilla", "Apple Color Emoji", "Segoe UI Emoji", '
          + '"Noto Color Emoji", sans-serif',
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
      {...rest}
    >
      {toFlagEmoji(alpha2)}
    </Box>
  );
}

/**
 * Convert an alpha-2 country code into the corresponding pair of Unicode
 * regional-indicator codepoints that render as a flag emoji.  Anything
 * not made of two A–Z letters falls back to the raw input.
 */
export function toFlagEmoji(alpha2: string): string {
  const code = (alpha2 ?? '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return alpha2;
  return Array.from(code)
    .map((c) => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)))
    .join('');
}
